import React from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Mail, ShieldCheck, KeyRound, Clock } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { t } from "@/i18n";

const inputCls = "bg-[#F5F0E6] border-[#D8CFBB] text-[#1A1610] focus-visible:ring-[#C5A880]";
const mmss = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

function OtpCountdown({ secondsLeft }) {
  const { lang } = useApp();
  if (secondsLeft > 0) {
    return (
      <span
        data-testid="otp-countdown"
        className={`inline-flex items-center gap-1 text-xs font-mono rounded-full px-2.5 py-1 ${
          secondsLeft < 60 ? "bg-red-100 text-red-700" : "bg-[#EBE3D3] text-[#4A3F30]"
        }`}
      >
        <Clock className="h-3 w-3" /> {mmss(secondsLeft)}
      </span>
    );
  }
  return (
    <span data-testid="otp-expired" className="text-xs text-red-700">
      {lang === "it" ? "Scaduto" : "Expired"}
    </span>
  );
}

export default function NDNCAOtpBox({
  otpRequested,
  otpEmail,
  otpCode,
  setOtpCode,
  secondsLeft,
  busy,
  canRequest,
  canSign,
  onRequest,
  onSign,
}) {
  const { lang } = useApp();
  return (
    <div className="rounded-md border border-[#D8CFBB] bg-[#FFFAF0] p-4">
      <div className="flex items-center gap-2 mb-3">
        <KeyRound className="h-4 w-4 text-[#C5A880]" />
        <p className="text-xs tracking-widest uppercase text-[#C5A880]">
          {lang === "it" ? "Firma con codice OTP" : "Sign with OTP"}
        </p>
      </div>
      {!otpRequested && (
        <Button
          onClick={onRequest}
          disabled={!canRequest || busy}
          data-testid="ndnca-request-otp"
          className="bg-[#1A1610] text-[#F5F0E6] hover:bg-[#2E2A22] rounded-full disabled:opacity-40"
        >
          <Mail className="h-4 w-4 me-2" />
          {busy ? "…" : t("otp_send_generic", lang)}
        </Button>
      )}
      {otpRequested && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-xs text-[#4A3F30]">
              {lang === "it" ? `Codice inviato a ${otpEmail}. Inseriscilo qui:` : `Code sent to ${otpEmail}:`}
            </p>
            <OtpCountdown secondsLeft={secondsLeft} />
          </div>
          <Input
            data-testid="ndnca-otp-input"
            className={`${inputCls} text-center text-lg tracking-[0.5em] font-mono`}
            maxLength={6}
            placeholder="000000"
            value={otpCode}
            onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
          />
          <div className="flex justify-between">
            <Button variant="ghost" size="sm" onClick={onRequest} disabled={busy} className="text-[#4A3F30] hover:bg-[#EBE3D3]">
              {lang === "it" ? "Rimanda codice" : "Resend code"}
            </Button>
            <Button
              onClick={onSign}
              disabled={otpCode.length !== 6 || busy || !canSign}
              data-testid="ndnca-sign-otp"
              className="bg-[#C5A880] text-black hover:bg-[#d9bf94] rounded-full disabled:opacity-40"
            >
              <ShieldCheck className="h-4 w-4 me-2" />
              {busy ? "…" : t("sign_ndnca_short", lang)}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
